import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import type { RoomId, ShaftId } from '../shared/ids';
import { roundQuantity } from '../schedule/unit-of-measure';
import { GEOMETRY_DECIMALS, type Room } from './room';
import { TAKEOFF_ERROR } from './errors';
import { isValidOutline, type Point } from './polygon';

/**
 * Shaft — a passagem na laje (Fase 24, D21).
 *
 * É um **buraco**, e por enquanto só isso: a prumada por onde sobem as instalações, sem
 * nada dentro que o desenho conheça. Mede a própria peça — área, contorno e contagem — e
 * tira a área dele do piso e do teto do ambiente em que está, porque ali não há laje nem
 * forro a fazer (RN-270, RN-271).
 *
 * **Não é a `PASSAGEM` do vão** (`OpeningKind`, em `wall.ts`). Aquela é a porta sem folha,
 * na parede, e desconta alvenaria; esta é a laje que não existe, e desconta piso. As duas
 * se chamariam "passagem" na obra, e é por isso que esta se chama shaft no código e
 * "Shaft (passagem na laje)" na tela: o mesmo nome para as duas mandaria o buraco da laje
 * para a conta da parede.
 *
 * > **A regra em uma linha (RN-271):** o shaft é do ambiente, e o ambiente perde o que o
 * > shaft ocupa — nunca abaixo de zero.
 */

export const SHAFT_LABEL = 'Shaft (passagem na laje)';

/**
 * O shaft desenhado — um retângulo na planta, dentro de um ambiente.
 *
 * Pertence ao **ambiente**, e pelo ambiente à tipologia (ADR-150). Não tem dono próprio
 * como a parede e o pilar, que precisam do pavimento tipo quando caem na divisa (RN-163): o
 * shaft não fica em cima de divisa nenhuma, ele fica no chão de um ambiente — e o da
 * circulação é o ambiente da tipologia de área comum, como a escada e o hall.
 */
export interface Shaft {
  readonly id: ShaftId;
  /** O ambiente em que o buraco está — é do piso e do teto dele que o shaft desconta. */
  readonly roomId: RoomId;
  readonly name?: string;
  /**
   * Dois cantos opostos do retângulo, em metros, no sistema de coordenadas da
   * **tipologia** — o mesmo do contorno do ambiente (ver `Room.outline`).
   *
   * É o gesto do pilar na prancha, de um canto ao outro, e guardar os dois cantos em vez de
   * largura e comprimento é o que devolve o shaft ao lugar dele no desenho: o buraco que
   * voltasse da recarga sem posição cairia no meio do ambiente, e o traço mentiria sobre
   * onde a prumada passa.
   */
  readonly from: Point;
  readonly to: Point;
  /**
   * Shafts iguais no mesmo ambiente — dois de 60 × 40 são um registro com `count: 2`, como
   * as duas janelas iguais da mesma parede (`Opening.count`).
   */
  readonly count: number;
  readonly notes?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export interface CreateShaftInput {
  readonly id: ShaftId;
  readonly roomId: RoomId;
  readonly from: Point;
  readonly to: Point;
  /** Ausente é um — o caso comum. */
  readonly count?: number;
  readonly name?: string;
  readonly notes?: string;
  readonly now: string;
}

function finito(ponto: Point): boolean {
  return Number.isFinite(ponto.x) && Number.isFinite(ponto.y);
}

export function createShaft(input: CreateShaftInput): Result<Shaft, DomainError> {
  // Canto sem número vira `NaN` na largura, e `NaN` não estoura: ele contamina o piso do
  // ambiente e toda soma que o encontra. Largura ou comprimento zero é o clique dobrado —
  // um shaft sem área que ainda assim contaria uma unidade.
  if (
    !finito(input.from) ||
    !finito(input.to) ||
    input.from.x === input.to.x ||
    input.from.y === input.to.y
  ) {
    return err(
      domainError(
        TAKEOFF_ERROR.INVALID_DIMENSION,
        'O shaft precisa de largura e comprimento maiores que zero.',
        { from: input.from, to: input.to },
      ),
    );
  }

  const count = input.count ?? 1;
  if (!Number.isInteger(count) || count < 1) {
    return err(
      domainError(
        TAKEOFF_ERROR.INVALID_QUANTITY,
        'A quantidade de shafts precisa ser um número inteiro de 1 para cima.',
        { count },
      ),
    );
  }

  const name = input.name?.trim();
  return ok({
    id: input.id,
    roomId: input.roomId,
    from: input.from,
    to: input.to,
    count,
    createdAt: input.now,
    updatedAt: input.now,
    ...(name === undefined || name === '' ? {} : { name }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  });
}

/** A largura do buraco, em metros — o lado que corre em `x`. */
export function shaftWidth(shaft: Pick<Shaft, 'from' | 'to'>): number {
  return Math.abs(shaft.to.x - shaft.from.x);
}

/** O comprimento do buraco, em metros — o lado que corre em `y`. */
export function shaftLength(shaft: Pick<Shaft, 'from' | 'to'>): number {
  return Math.abs(shaft.to.y - shaft.from.y);
}

/**
 * A área do shaft, já vezes a quantidade (RN-270) — e é ela que o piso perde.
 *
 * Arredonda uma vez, no fim: largura e comprimento saem de coordenadas em ponto flutuante,
 * e 1,3 − 0,7 dá 0,6000000000000001. Arredondar cada lado antes multiplicaria o erro.
 */
export function shaftArea(shaft: Pick<Shaft, 'from' | 'to' | 'count'>): number {
  return roundQuantity(shaftWidth(shaft) * shaftLength(shaft) * shaft.count, GEOMETRY_DECIMALS);
}

/**
 * O contorno do shaft em metro linear, vezes a quantidade — o **requadro** (RN-270).
 *
 * É a borda da laje cortada, que alguém arremata; o perímetro do retângulo, e não o do
 * ambiente em volta dele.
 */
export function shaftContour(shaft: Pick<Shaft, 'from' | 'to' | 'count'>): number {
  return roundQuantity(
    2 * (shaftWidth(shaft) + shaftLength(shaft)) * shaft.count,
    GEOMETRY_DECIMALS,
  );
}

/** O que os shafts de um desenho medem, somados. */
export interface ShaftTotals {
  /** Metro quadrado de buraco — a área que piso e teto perderam. */
  readonly area: number;
  /** Metro linear de requadro. */
  readonly contour: number;
  /** Quantos shafts. */
  readonly count: number;
}

export const NO_SHAFTS: ShaftTotals = { area: 0, contour: 0, count: 0 };

/**
 * O ponto está dentro do contorno? Pelo raio que atravessa as bordas — par é fora, ímpar é
 * dentro. Vale para o L e para qualquer forma que o traçado produza.
 */
function dentro(ponto: Point, contorno: readonly Point[]): boolean {
  let cruzou = false;
  for (let i = 0, j = contorno.length - 1; i < contorno.length; j = i, i += 1) {
    const a = contorno[i]!;
    const b = contorno[j]!;
    if (a.y > ponto.y !== b.y > ponto.y) {
      const x = ((b.x - a.x) * (ponto.y - a.y)) / (b.y - a.y) + a.x;
      if (ponto.x < x) cruzou = !cruzou;
    }
  }
  return cruzou;
}

/**
 * O ambiente do shaft traçado: o que contém o **centro** do retângulo.
 *
 * O centro, e não os quatro cantos: o shaft desenhado rente à parede tem dois cantos em
 * cima dela, e pedir que todos caíssem dentro recusaria justamente o caso comum. Fora de
 * qualquer ambiente traçado não há de quem descontar, e a resposta é nenhum.
 */
export function roomOfShaft(
  shaft: Pick<Shaft, 'from' | 'to'>,
  rooms: readonly Room[],
): Room | undefined {
  const centro = { x: (shaft.from.x + shaft.to.x) / 2, y: (shaft.from.y + shaft.to.y) / 2 };
  return rooms.find(
    (room) =>
      room.deletedAt === undefined &&
      room.outline !== undefined &&
      isValidOutline(room.outline) &&
      dentro(centro, room.outline),
  );
}
